import type { RnbCalculatorPayload } from 'typings'
import { toAPIOriginalValues, toAPIPromotionBase } from '..'
import {
  BuyAndWinBenefit,
  BuyAndWinBenefitVariant,
  BuyAndWinFreeShipping,
  BuyAndWinTrigger,
  MaximumUnitPriceDiscount,
  ShippingApplyTo,
  type BuyAndWinEffect,
  type BuyAndWinPromotion,
} from '../../../../typings'
import { toAPIConditions } from '../conditions'
import { toAPIGift } from '../gift'
import { toAPIRestrictions } from '../restrictions'

const BUY_AND_WIN_INCOMPATIBLE_FIELDS: (keyof RnbCalculatorPayload)[] = [
  // Discount section
  'absoluteShippingDiscountValue',
  'nominalDiscountType',
  'nominalShippingDiscountValue',
  'nominalDiscountValue',
  'maximumUnitPriceDiscount',
  'percentualDiscountValue',
  'rebatePercentualDiscountValue',
  'percentualShippingDiscountValue',
  'percentualTax',
  'shippingPercentualTax',
  'taxExpression',
  'discountExpression',
  'percentualDiscountValueList1',
  'percentualDiscountValueList2',
  'activateGiftsMultiplier',
  'nominalRewardValue',
  'percentualRewardValue',
  'orderStatusRewardValue',
  'nominalTax',
  'priceTableName',
  'percentualDiscountValueList',

  // Buy and win section
  'skusGift',
  'collections1BuyTogether',
  'collections2BuyTogether',
  'minimumQuantityBuyTogether',
  'quantityToAffectBuyTogether',
  'enableBuyTogetherPerSku',
  'listSku1BuyTogether',
  'listSku2BuyTogether',
]

type APIBuyAndWinSectionPayload = Pick<
  Partial<RnbCalculatorPayload>,
  | 'listSku1BuyTogether'
  | 'minimumQuantityBuyTogether'
  | 'collections1BuyTogether'
  | 'skusGift'
  | 'percentualShippingDiscountValue'
  | 'maximumUnitPriceDiscount'
  | 'activateGiftsMultiplier'
  | 'applyToAllShippings'
  | 'isSlaSelected'
  | 'slasIds'
  | 'enableBuyTogetherPerSku'
>

export function toAPIFreeShipping(
  freeShipping: BuyAndWinFreeShipping
): Pick<
  APIBuyAndWinSectionPayload,
  | 'percentualShippingDiscountValue'
  | 'applyToAllShippings'
  | 'isSlaSelected'
  | 'slasIds'
> {
  const base = {
    percentualShippingDiscountValue: 100,
  }

  switch (freeShipping.applyTo) {
    case ShippingApplyTo.all:
      return {
        ...base,
        applyToAllShippings: true,
      }
    case ShippingApplyTo.cheapest:
      return {
        ...base,
        applyToAllShippings: false,
      }
    case ShippingApplyTo.selected:
      return {
        ...base,
        isSlaSelected: false,
        slasIds: freeShipping.selectedMethods.map((method) => method.id),
      }
  }
}

export function toAPIMaximumUnitPriceDiscount(
  benefit: MaximumUnitPriceDiscount
): Pick<APIBuyAndWinSectionPayload, 'maximumUnitPriceDiscount'> {
  return {
    maximumUnitPriceDiscount: benefit.price,
  }
}

export function toAPIBenefit(
  benefit: BuyAndWinBenefit
): ReturnType<
  | typeof toAPIGift
  | typeof toAPIFreeShipping
  | typeof toAPIMaximumUnitPriceDiscount
> {
  switch (benefit.type) {
    case BuyAndWinBenefitVariant.GIFT:
      return toAPIGift(benefit)
    case BuyAndWinBenefitVariant.FREE_SHIPPING:
      return toAPIFreeShipping(benefit)
    case BuyAndWinBenefitVariant.MAXIMUM_UNIT_PRICE_DISCOUNT:
      return toAPIMaximumUnitPriceDiscount(benefit)
  }
}

export function toAPITrigger(
  trigger: BuyAndWinTrigger
): Pick<
  APIBuyAndWinSectionPayload,
  | 'listSku1BuyTogether'
  | 'minimumQuantityBuyTogether'
  | 'collections1BuyTogether'
  | 'enableBuyTogetherPerSku'
> {
  return {
    listSku1BuyTogether: trigger.skus,
    collections1BuyTogether: trigger.collections,
    minimumQuantityBuyTogether: trigger.minQuantity,
    enableBuyTogetherPerSku: trigger.sameItemsOnly,
  }
}

export function toAPIBuyAndWinEffect(
  config: BuyAndWinEffect
): APIBuyAndWinSectionPayload {
  const { trigger, benefit } = config
  if (!trigger || !benefit)
    throw new Error(
      'Cannot convert Promotion to API Payload: Invalid Buy And Win Effect',
      { cause: { payload: config } }
    )

  return {
    ...toAPITrigger(trigger),
    ...toAPIBenefit(benefit),
  }
}

type APIBuyAndWinPayload = ReturnType<
  typeof toAPIOriginalValues &
    typeof toAPIBuyAndWinEffect &
    typeof toAPIConditions &
    typeof toAPIRestrictions &
    typeof toAPIPromotionBase
>

export function toAPIBuyAndWin(
  data: BuyAndWinPromotion,
  originalValues?: RnbCalculatorPayload
): APIBuyAndWinPayload {
  return {
    ...toAPIOriginalValues(originalValues, BUY_AND_WIN_INCOMPATIBLE_FIELDS),
    ...toAPIPromotionBase(data),
    ...toAPIConditions(data.conditions),
    ...toAPIRestrictions(data.restrictions),
    ...toAPIBuyAndWinEffect(data.buyAndWin),
  }
}
